/**
 * What stands behind and under the title and story figures in place of the stage: a dark wall where
 * the stage's back wall would be, a starfield in front of it (as the C64's story screen has, and the
 * ending, endingView.ts) and, on the title, a floor of pillow blocks for the dragons to stand on.
 *
 * The wall matters: the post pass fogs every pixel by its depth, and a pixel with nothing behind it
 * counts as infinitely far, so glass drawn over the void would be fogged away to a dark smudge.
 */

import { type BufferGeometry, Color, type Material, Mesh, Vector3 } from 'three';
import { MeshBuilder, place } from '../kit/MeshBuilder';
import type { Palette } from '../palette';

/** A small fixed LCG: the same sky every time (captures must match). */
export function lcg(seed: number): () => number {
  let s = seed >>> 0;
  return () => {
    s = (Math.imul(s, 1103515245) + 12345) >>> 0;
    return s / 0x100000000;
  };
}

export interface StarKind {
  /** Palette reference of the colour. */
  colour: string;
  /** Speck width and height, tiles. */
  w: number;
  h: number;
}

export interface StarOptions {
  count: number;
  seed: number;
  kinds: readonly StarKind[];
  /** Half the width and height of the field, tiles, centred on the origin. */
  halfW: number;
  halfH: number;
  /** Depth layers the stars are dealt into, one mesh each: the first at z0, each next dz nearer. */
  layers: number;
  z0: number;
  dz: number;
}

/** Self-lit specks scattered over a field, one mesh per depth layer. */
export function buildStars(p: Palette, glow: Material, o: StarOptions): Mesh[] {
  const rand = lcg(o.seed);
  const builders = Array.from({ length: o.layers }, () => new MeshBuilder());
  for (let i = 0; i < o.count; i++) {
    const kind = o.kinds[Math.floor(rand() * o.kinds.length)] ?? o.kinds[0];
    const mb = builders[i % o.layers];
    const x = (rand() * 2 - 1) * o.halfW;
    const y = (rand() * 2 - 1) * o.halfH;
    if (!kind || !mb) continue;
    mb.box(kind.w, kind.h, Math.min(kind.w, kind.h), p.color(kind.colour), place(x, y, 0), true);
  }
  return builders.map((mb, layer) => {
    const mesh = new Mesh(mb.build(), glow);
    mesh.position.z = o.z0 + layer * o.dz;
    return mesh;
  });
}

/** Where the wall stands: the stage's back wall's depth (05 §3.1), short of where the fog starts. */
export const WALL_Z = -2;

/**
 * A wall filling the view, facing the camera, at WALL_Z, in the level look's fog colour: what the
 * void around the stage fades to (near black; black in the 1987 look, as the C64's screen).
 */
export function buildWall(p: Palette, halfW: number, halfH: number): BufferGeometry {
  const mb = new MeshBuilder();
  const c = new Color(p.fog);
  const v = (x: number, y: number): Vector3 => new Vector3(x, y, WALL_Z);
  mb.quad(v(-halfW, -halfH), v(halfW, -halfH), v(halfW, halfH), v(-halfW, halfH), c);
  return mb.build();
}

/**
 * One row of pillow blocks, a tile each, with their tops at `top`, from -halfW to +halfW: a box in
 * the ramp's middle with a lighter top, and a raised bevelled cushion on the face toward the camera.
 */
export function buildFloor(p: Palette, ramp: string, top: number, halfW: number): BufferGeometry {
  const mb = new MeshBuilder();
  const dark = p.color(`${ramp}.0`);
  const mid = p.color(`${ramp}.1`);
  const light = p.color(`${ramp}.2`);
  for (let x = -halfW; x < halfW; x++) {
    const cx = x + 0.5;
    mb.box(1, 1, 1.5, { all: mid, top: light, bottom: dark, front: dark }, place(cx, top - 1, 0));
    const cushion = { all: mid, top: light, bottom: dark, left: light, right: dark };
    mb.box(0.84, 0.84, 0.1, cushion, place(cx, top - 0.92, 0.8));
    mb.blob(0.3, 0.3, 0.07, light, place(cx - 0.06, top - 0.44, 0.9), { detail: 2 });
  }
  return mb.build();
}
